import { Box, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { MenuItem } from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import useColors from "../../api/hooks/useColors";

export const SidebarMenuItem = ({
  title,
  path,
  icon,
  collapsed,
  notPermitted,
}) => {
  const colors = useColors();
  const itemRef = useRef(null);
  const location = useLocation();

  let onboarding = location.pathname.includes("onboarding");
  let active = false;
  if (path === "/") {
    if (path === "/" && location.pathname === path) active = true;
  } else active = location.pathname.includes(path);

  if (onboarding) {
    // Check if this menu item corresponds to the current onboarding path
    // For example, if we're at /onboarding/posture/controls, highlight the /posture/controls item
    const onboardingPath = location.pathname.replace("/onboarding", "");
    if (path !== "/" && onboardingPath.includes(path)) {
      active = true;
    }
  }

  // Effect to scroll the active item into view, especially important during onboarding
  useEffect(() => {
    if (active && itemRef.current) {
      // Use a short timeout to ensure the sidebar has rendered properly
      const timer = setTimeout(() => {
        itemRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [active, location.pathname]);

  const handleItemClick = () => {
    // Wait for the next render cycle to ensure the DOM is updated
    setTimeout(() => {
      if (itemRef.current) {
        // Directly scroll the item into view with smooth behavior
        itemRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      }
    }, 100);
  };

  return (
    <MenuItem
      ref={itemRef}
      style={{
        zIndex: active && onboarding ? 1000 : 0,
        border:
          active && onboarding ? "1px solid " + colors.primary_200 : "none",
        boxShadow:
          active && onboarding ? "0 0 18px -3px " + colors.primary_200 : "none",
        color: active
          ? colors.primary_main
          : notPermitted
          ? colors.action_disabled
          : colors.contrast_text_dark,
        borderRadius: "8px",
        minHeight: "70px",
        height: "32px",
        padding: "0px",
        width: active && onboarding ? "90%" : "100%",
        margin: active && onboarding ? "5px" : "0px",
        cursor: notPermitted ? "auto" : "pointer",
        transition: "all 0.3s ease",
        ...(active && onboarding && { transform: "scale(1.05)" }),
      }}
      active={active}
      onClick={notPermitted || onboarding ? () => {} : handleItemClick}
      component={notPermitted || onboarding ? null : <Link to={path} />}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            borderRadius: "8px",
            height: "100%",
            padding: "8px",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: active
              ? colors.primary_8percent
              : colors.contrast_light,
            ...(!notPermitted && {
              "&:hover": {
                backgroundColor: colors.action_hover,
              },
            }),
          }}
        >
          {React.cloneElement(icon, {
            color: active ? colors.primary_main : colors.contrast_text_dark,
          })}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: "10px",
            wordBreak: "break-word",
          }}
        >
          {title}
        </Typography>
      </Box>
    </MenuItem>
  );
};
